import { useState, useEffect } from 'react'
import { resolveVisibility } from "../../../../common/model/condition/evaluate"
import styled from 'styled-components'
import useTranslation from '../../hooks/useTranslation'
import useApi from '../../hooks/useApi'
import { useQuery } from '@tanstack/react-query'
import { pluginFor } from './plugins'
import { useStore } from '../pageView/Page'

const Card = styled.div`
    background: ${({ theme }) => theme.card.background};
    border: 1px solid ${({ theme }) => theme.form.border};
    border-radius: 1rem;
    padding: 1rem;
    margin: 0 0.5rem;
    box-shadow: ${({ theme }) => theme.card.lightBoxShadow};
`

const Title = styled.h2`
    font-size: 1.05rem;
    font-weight: 700;
    color: ${({ theme }) => theme.font.color};
    margin: 0 0 0.75rem;
`

const Divider = styled.hr`
    border: none;
    border-top: 1px solid ${({ theme }) => theme.form.border};
    margin: 0.5rem 0 0.75rem;
`

const TwoCol = styled.div`
    display: flex;
    gap: 1rem;
    align-items: flex-start;
`

const FieldsCol = styled.div`
    flex: 1;
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
`

const FieldLabel = styled.div`
    font-size: 0.75rem;
    color: ${({ theme }) => theme.font.label};
    margin-bottom: 0.1rem;
`

const FieldValue = styled.div`
    font-size: 0.9rem;
    color: ${({ theme }) => theme.font.color};
    font-weight: 500;
`

const QtySplitGrid = styled.div`
    display: grid;
    grid-template-columns: 1fr 1fr;
    grid-template-rows: auto auto auto;
    column-gap: 0.75rem;
    row-gap: 0.2rem;
    align-items: center;
`

const QtyReadOnly = styled.div`
    font-size: 1.4rem;
    font-weight: 700;
    color: ${({ theme }) => theme.font.label};
    padding: 0.3rem 0.5rem;
    border: 1px solid ${({ theme }) => theme.form.border};
    border-radius: 0.375rem;
    text-align: center;
    background: ${({ theme }) => theme.form.readOnlyBg};
`

const StepperRow = styled.div`
    display: flex;
    align-items: center;
    gap: 0.25rem;
`

const StepButton = styled.button`
    width: 2rem;
    height: 2rem;
    font-size: 1.1rem;
    font-weight: 700;
    border: 1px solid ${({ theme }) => theme.form.border};
    border-radius: 0.375rem;
    background: ${({ theme }) => theme.form.textControl.background};
    color: ${({ theme }) => theme.font.color};
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    &:hover { border-color: ${({ theme }) => theme.form.activeBorder}; }
`

const QtyInput = styled.input`
    width: 3.5rem;
    padding: 0.3rem 0.25rem;
    font-size: 1.3rem;
    font-weight: 700;
    text-align: center;
    border: 1px solid ${({ theme }) => theme.form.border};
    border-radius: 0.375rem;
    background: ${({ theme }) => theme.form.textControl.background};
    color: ${({ theme }) => theme.form.font};
    appearance: none;
    -moz-appearance: textfield;
    &::-webkit-inner-spin-button,
    &::-webkit-outer-spin-button { display: none; }
    &:focus { outline: none; border-color: ${({ theme }) => theme.form.activeBorder}; }
`

const UomLabel = styled.span`
    font-size: 0.75rem;
    color: ${({ theme }) => theme.font.label};
    text-transform: uppercase;
    text-align: center;
`

const RemainingNote = styled.div`
    font-size: 0.75rem;
    color: ${({ theme }) => theme.font.label};
    font-style: italic;
    margin-top: 0.25rem;
`

const ButtonRow = styled.div`
    display: flex;
    gap: 0.75rem;
    margin-top: 1rem;
    justify-content: space-between;
`

const ActionButton = styled.button`
    padding: 0.5rem 1.1rem;
    font-size: 0.88rem;
    font-weight: 600;
    border: none;
    border-radius: 999px;
    cursor: pointer;
    background: ${({ theme, $variant }) => $variant === 'secondary' ? theme.btn.secondary : theme.btn.primary};
    color: ${({ theme }) => theme.btn.font};
    &:hover { opacity: 0.88; }
`

const DoneLabel = styled.div`
    font-size: 0.75rem;
    font-weight: 700;
    color: ${({ theme }) => theme.btn.primary};
    margin-bottom: 0.25rem;
`

const isButtonVisible = (btn, isDone) =>
    resolveVisibility(btn, { values: {}, context: { done: isDone } }) === 'show'

const ProcessItemDetail = ({
    viewKey, section, lines, ui, onAction, linesField, itemInput }) => {
    const { t } = useTranslation()
    const { getMe } = useApi()

    const lineId    = ui[section.type]
    const line      = lines?.find(l => l.id === lineId)
    const lineIndex = lines?.findIndex(l => l.id === lineId) ?? -1

    const isDone = !!line?.[section.doneField]

    const bodySection      = section.blocks?.find(s => s.type === 'body')
    const titleSection     = section.blocks?.find(s => s.type === 'title')

    const qtyField = bodySection?.columns
        .flatMap(c => c.elements || [])
        .find(f => f.type === 'quantitySplit' || f.type === 'quantityInput')

    const plannedQty    = (line?.[qtyField?.planned ?? qtyField?.value ?? 'quantity']) ?? 0
    const initialActual = isDone ? (line?.[qtyField?.actual] ?? plannedQty) : plannedQty

    const [enteredQty,         setEnteredQty]         = useState(initialActual)

    const locationId    = itemInput?.location ? (line?.[itemInput.location] ?? null) : null

    const query  = section.query
    const itemId = query ? line?.[query.paramValue] : null

    const { data: queryData } = useQuery({
        queryKey: [query?.endpoint, itemId],
        queryFn:  () => getMe(`${query.endpoint}?${query.endpointParam}=${itemId}`),
        enabled:  !!query && !!itemId,
    })

    // The section's declared query, reduced to the shape a plugin can use: rows,
    // their total, how many there are, and an index by whatever key the query
    // named. What those mean is the query's business, not this component's.
    const rows = query
        ? (Array.isArray(queryData?.data) ? queryData.data : []).filter(r => r[query.resultQtyField] > 0)
        : []
    const result = {
        rows,
        total: rows.reduce((sum, r) => sum + (r[query?.resultQtyField] || 0), 0),
        count: rows.length,
        byKey: Object.fromEntries(rows.map(r => [r[query?.resultLocationField], r[query?.resultQtyField]])),
    }

    // An item cannot be taken in a greater quantity than the chosen key holds.
    const available = locationId != null ? (result.byKey[locationId] ?? null) : null
    const maxQty = available !== null ? Math.min(plannedQty, available) : plannedQty

    useEffect(() => {
        if (!isDone) setEnteredQty(q => q > maxQty ? maxQty : q)
    }, [maxQty, isDone])

    if (!line) return null

    const isSplit   = !isDone && enteredQty < plannedQty
    const remaining = plannedQty - enteredQty

    const visibleButtons = (section.buttons || []).filter(btn => isButtonVisible(btn, isDone))
    const leftButtons    = visibleButtons.filter(b => b.position === 'left')
    const rightButtons   = visibleButtons.filter(b => b.position !== 'left')

    const getLabel = (btn) => (btn.splitLabel && isSplit) ? t(btn.splitLabel) : t(btn.label)

    const handleButtonClick = (btn) => {
        onAction(btn.effects, line.id, enteredQty, locationId)
    }

    // Blocks are singletons within a section, so their ids need no row number:
    // `warehousePick.itemDetail.fromLocationId`.
    const blockTestId = (b) => b && `${viewKey}.${section.id}.${b.id}`

    // Plugins are resolved by name from the registry, never imported here — a
    // process view that declares none pulls in no domain component at all.
    const renderPlugin = (b, key) => {
        const Plugin = pluginFor(b.plugin)
        if (!Plugin) return null
        return (
            <Plugin
                key={key}
                block={b}
                line={line}
                lineIndex={lineIndex}
                linesField={linesField}
                testId={blockTestId(b)}
                useStore={useStore}
                result={result}
                itemId={itemId}
                itemName={line?.[titleSection?.value]}
            />
        )
    }

    const renderField = (field) => {
        if (field.type === 'field') {
            return (
                <div key={field.value}>
                    <FieldLabel>{t(field.label)}</FieldLabel>
                    <FieldValue>{line[field.value]}</FieldValue>
                </div>
            )
        }

        if (field.type === 'quantitySplit') {
            const uom = line[field.decorator] || ''
            return (
                <div key="quantitySplit">
                    <QtySplitGrid>
                        <FieldLabel style={{ alignSelf: 'end' }}>{t(field.plannedLabel)}</FieldLabel>
                        <FieldLabel style={{ alignSelf: 'end' }}>{t(field.actualLabel)}</FieldLabel>

                        <QtyReadOnly>{plannedQty}</QtyReadOnly>
                        <StepperRow>
                            <StepButton
                                type="button"
                                data-testid={`${blockTestId(bodySection)}.qty.minus`}
                                disabled={isDone || enteredQty <= 0}
                                onClick={() => setEnteredQty(q => Math.max(0, parseFloat((q - 1).toFixed(5))))}
                            >−</StepButton>
                            <QtyInput
                                type="number"
                                data-testid={`${blockTestId(bodySection)}.qty`}
                                min={0}
                                max={maxQty}
                                value={enteredQty}
                                readOnly={isDone}
                                onChange={e => {
                                    const val = parseFloat(e.target.value)
                                    if (!isNaN(val)) setEnteredQty(Math.max(0, Math.min(val, maxQty)))
                                }}
                            />
                            <StepButton
                                type="button"
                                data-testid={`${blockTestId(bodySection)}.qty.plus`}
                                disabled={isDone || enteredQty >= maxQty}
                                onClick={() => setEnteredQty(q => Math.min(maxQty, parseFloat((q + 1).toFixed(5))))}
                            >+</StepButton>
                        </StepperRow>

                        <UomLabel>{uom}</UomLabel>
                        <UomLabel>{uom}</UomLabel>
                    </QtySplitGrid>
                    {isSplit && (
                        <RemainingNote>
                            {t(field.remainingLabel)}: {parseFloat(remaining.toFixed(5))} {uom}
                        </RemainingNote>
                    )}
                </div>
            )
        }

        if (field.type === 'plugin') return renderPlugin(field, field.plugin)

        return (
            <div key={field.value ?? field.type}>
                <FieldLabel>{t(field.label)}</FieldLabel>
                <FieldValue>{line[field.value]}</FieldValue>
            </div>
        )
    }

    const renderColumn = (col, i) => (
        <FieldsCol key={i}>
            {(col.elements || []).map(field => renderField(field))}
        </FieldsCol>
    )

    const renderSection = (s, i) => {
        if (s.type === 'title') {
            return (
                <div key={i}>
                    <Title>{line[s.value]}</Title>
                    <Divider />
                </div>
            )
        }

        if (s.type === 'body') {
            return (
                <TwoCol key={i}>
                    {s.columns.map((col, j) => renderColumn(col, j))}
                </TwoCol>
            )
        }


        if (s.type === 'plugin') return renderPlugin(s, i)

        return null
    }

    return (
        <Card>
            {isDone && <DoneLabel>{t(section.doneLabel)}</DoneLabel>}
            {(section.blocks || []).map((s, i) => renderSection(s, i))}
            <ButtonRow>
                <div style={{ display: 'flex', gap: '0.5rem' }}>
                    {leftButtons.map((btn, i) => (
                        <ActionButton key={i} type="button" $variant={btn.variant} data-testid={btn.id} onClick={() => handleButtonClick(btn)}>
                            {getLabel(btn)}
                        </ActionButton>
                    ))}
                </div>
                <div style={{ display: 'flex', gap: '0.5rem' }}>
                    {rightButtons.map((btn, i) => (
                        <ActionButton key={i} type="button" $variant={btn.variant} data-testid={btn.id} onClick={() => handleButtonClick(btn)}>
                            {getLabel(btn)}
                        </ActionButton>
                    ))}
                </div>
            </ButtonRow>
        </Card>
    )
}

export default ProcessItemDetail
